/**
 * THE REEL'S CLOCK ARITHMETIC. A scene is a pure function of its progress `p`
 * (0 → 1), so any moment can be drawn on its own: paused, stepped, or checked
 * by eye at an exact second. Nothing here keeps state.
 */
import type { CSSProperties } from "react";

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);

/** Progress through the window [a, b] of a scene: 0 before it, 1 after it. */
export const span = (p: number, a: number, b: number): number => clamp01((p - a) / (b - a));

export const easeOut = (x: number): number => 1 - Math.pow(1 - x, 3);
export const easeInOut = (x: number): number => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

/** A line arriving: it fades up a few pixels into place. */
export const appear = (x: number): CSSProperties => {
  const e = easeOut(x);
  return { opacity: e, transform: `translateY(${Math.round((1 - e) * 60) / 10}px)` };
};

/** The words typed so far. */
export const typed = (text: string, x: number): string => text.slice(0, Math.round(text.length * clamp01(x)));

/** A count rising to its total. */
export const counted = (total: number, x: number): number => Math.round(total * easeOut(clamp01(x)));

/**
 * The working marker's breath, drawn from the scene's own clock rather than a CSS
 * loop: paused, it holds still, because nothing on the page may move while no work
 * is shown happening.
 */
export const breathe = (p: number, cycles = 10): number => 0.4 + 0.6 * (0.5 + 0.5 * Math.cos(2 * Math.PI * cycles * p));

/** The typing caret, on for one beat and off for the next, by the same clock. */
export const caretOn = (p: number, beats = 36): boolean => Math.floor(p * beats) % 2 === 0;
